import { Plus, Settings2 } from '@tamagui/lucide-icons';
import { useParentScrollableRef } from '@unistocks/components/basic/scrollview';
import { Button } from '@unistocks/components/button/button';
import { ValidationMessage } from '@unistocks/components/form/components/validation-message';
import { Form } from '@unistocks/components/form/form';
import { oneOf } from '@unistocks/components/form/validators';
import { Column, Row } from '@unistocks/components/layout/flex';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { H4 } from '@unistocks/components/text/text';
import {
  CalculatedValueArrayItem,
  EventDraggableItem
} from '@unistocks/features/dashboard/components/dashboard-data/event-view/draggable-item';
import { EventSettingsForm } from '@unistocks/features/dashboard/components/dashboard-data/event-view/event-settings-form';
import { eventViewLayoutOptions } from '@unistocks/features/dashboard/components/dashboard-data/event-view/layout';
import { EventViewLayoutSelect } from '@unistocks/features/dashboard/components/dashboard-data/event-view/layouts/layout-select';
import { getEmptyCalculatedValue } from '@unistocks/features/dashboard/components/dashboard-data/event-view/utils';
import { selectSelectedPortfolioId } from '@unistocks/features/portfolios/store/portfolio.slice';
import { t } from '@unistocks/localization';
import { CalculatedValueDto, DashboardItemSettingsDto } from '@unistocks/proxy/axios-client';
import { useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { triggerHaptics } from '@unistocks/shared/utils/haptics/haptics';
import { extractId } from '@unistocks/shared/utils/list-utils';
import { isNumber } from '@unistocks/shared/utils/util';
import React, { useCallback, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useFormState, useWatch } from 'react-hook-form';
import Sortable, {
  ActiveItemDroppedCallback,
  DragStartCallback,
  SortableGridRenderItem
} from 'react-native-sortables';
import { Separator } from 'tamagui';
import { z } from 'zod';

export const eventViewSchemaFactory = () =>
  z.object({
    title: z.string().nullish(),
    layout: oneOf(
      eventViewLayoutOptions.map(({ value }) => value),
      t('::ValidationError:Required:Selection', { 0: t('::Layout') })
    ),
    values: z
      .array(z.record(z.string(), z.any()))
      .min(1, t('::The {0} field is required.', { 0: t('::Dashboard:Settings:EventView:Events') }))
  });

export const EventViewSettingsContentStep: React.FC = () => {
  const form = useFormContext<DashboardItemSettingsDto>();
  const defaultPortfolio = useAppSelector(selectSelectedPortfolioId);
  const [{ activeItem, index }, setActiveItem] = useState<{
    activeItem?: CalculatedValueDto;
    index?: number;
  }>({});
  const {
    fields: values,
    append,
    update,
    remove,
    move
  } = useFieldArray({ control: form.control, name: 'values' });

  const handleCalculatedValueSubmit = (value: CalculatedValueDto) => {
    if (!values) return;

    if (isNumber(index)) {
      update(index, value);
    } else {
      append(value);
    }

    setActiveItem({});
  };

  return (
    <>
      <Form.Input
        name={'title'}
        label={t('::DisplayName')}
        helpText={t('::Dashboard:Settings:Title:HelpText')}
      />

      <>
        <Row justifyContent="space-between" alignItems="center">
          <H4>{t('::Dashboard:Settings:EventView:Events')}</H4>

          <Button
            withScale
            withFeedback
            circular
            theme={'green'}
            variant="outlined"
            size={'$2.5'}
            Icon={Plus}
            scaleIcon={2}
            hitSlop={20}
            onPress={() => {
              const newItem = getEmptyCalculatedValue();
              if (defaultPortfolio) newItem.params?.portfolios?.push(defaultPortfolio);

              setActiveItem({
                activeItem: newItem
              });
            }}
          />
        </Row>

        <Separator mt={'$2'} />

        <EventList
          setActiveItem={setActiveItem}
          values={values}
          remove={remove}
          move={move}
        />

        <FullScreenSheet
          open={!!activeItem}
          onOpenChange={() => setActiveItem({})}
          headerIcon={<HeaderIcon Icon={Settings2} />}
          headerTitle={
            isNumber(index)
              ? t('::EditWhat', { 0: activeItem?.displayName })
              : t('::Dashboard:Settings:EventView:Add')
          }
        >
          <EventSettingsForm
            isEdit={isNumber(index)}
            initialValues={activeItem!}
            onSubmit={handleCalculatedValueSubmit}
            onCancel={() => setActiveItem({})}
          />
        </FullScreenSheet>
      </>
    </>
  );
};

export const EventViewSettingsLayoutStep: React.FC = () => {
  const { setValue } = useFormContext<DashboardItemSettingsDto>();
  const layout = useWatch<DashboardItemSettingsDto, 'layout'>({ name: 'layout' });

  return <EventViewLayoutSelect value={layout} onChange={value => setValue('layout', value)} />;
};

type EventListProps = {
  setActiveItem: (state: { activeItem?: CalculatedValueDto; index?: number }) => void;
  values: ReturnType<typeof useFieldArray<DashboardItemSettingsDto, 'values'>>['fields'];
  remove: ReturnType<typeof useFieldArray<DashboardItemSettingsDto, 'values'>>['remove'];
  move: ReturnType<typeof useFieldArray<DashboardItemSettingsDto, 'values'>>['move'];
};

const EventList: React.FC<EventListProps> = ({ setActiveItem, values, remove, move }) => {
  const { control } = useFormContext<DashboardItemSettingsDto>();
  const dragActive = useRef(false);
  const scrollRef = useParentScrollableRef();
  const { errors, isValid } = useFormState({ control, name: 'values' });
  const errorMessage = !isValid && errors?.values?.message;

  const renderItem = useCallback<SortableGridRenderItem<CalculatedValueArrayItem>>(
    ({ item, index }) => (
      <EventDraggableItem
        item={item}
        onPress={() => {
          // Prevent triggering onPress after dragging (web only)
          if (dragActive.current) {
            dragActive.current = false;
            return;
          }
          triggerHaptics();
          setActiveItem({ activeItem: item, index });
        }}
        onDeletePress={() => remove(index)}
      />
    ),
    [remove, setActiveItem]
  );

  const onDragStart = useCallback<DragStartCallback>(() => {
    dragActive.current = true;
  }, []);

  const onDragEnd = useCallback<ActiveItemDroppedCallback>(
    ({ fromIndex, toIndex }) => {
      move(fromIndex, toIndex);
    },
    [move]
  );

  return (
    <Column gap={'$2'} mb={'$3'}>
      <Sortable.Grid
        scrollableRef={scrollRef}
        data={values}
        keyExtractor={extractId}
        renderItem={renderItem}
        hapticsEnabled
        enableActiveItemSnap
        activeItemScale={0.9}
        showDropIndicator
        dragActivationDelay={500}
        overDrag="vertical"
        onDragStart={onDragStart}
        onActiveItemDropped={onDragEnd}
      />
      {errorMessage ? <ValidationMessage>{errorMessage}</ValidationMessage> : null}
    </Column>
  );
};

EventViewSettingsContentStep.displayName = 'EventViewSettingsContentStep';
EventViewSettingsLayoutStep.displayName = 'EventViewSettingsLayoutStep';
EventList.displayName = 'EventList';
